import React, { useState, useRef } from 'react';
import api from '../utils/api';
import { ObjectUploader } from './ObjectUploader';

interface ResidentRegistrationFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (resident: any) => void;
}

const ResidentRegistrationForm: React.FC<ResidentRegistrationFormProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    name: '',
    dateOfBirth: '',
    diagnosis: '',
    photo: '',
    emergencyContact: {
      name: '',
      phone: '',
      relationship: ''
    },
    admissionDate: new Date().toISOString().split('T')[0] // Today's date in YYYY-MM-DD format
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [photoURL, setPhotoURL] = useState<string | null>(null);
  const [registeredResidentId, setRegisteredResidentId] = useState<number | null>(null);
  const uploadedKeyRef = useRef<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name.startsWith('emergencyContact.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        emergencyContact: {
          ...prev.emergencyContact,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleGetUploadParameters = async () => {
    const response = await api.post('/objects/upload');
    // Persist the storage key so we can save it to the resident after upload
    uploadedKeyRef.current = response.data.objectKey;
    return {
      method: 'PUT' as const,
      url: response.data.uploadURL,
    };
  };

  const handleUploadComplete = async (result: any) => {
    if (result.successful && result.successful[0] && registeredResidentId) {
      const objectKey = uploadedKeyRef.current;
      try {
        await api.put(`/residents/${registeredResidentId}/photo`, { objectKey });
        setPhotoURL(objectKey);
        alert('Photo uploaded successfully! You can now close this dialog.');
      } catch (error) {
        console.error('Error updating photo:', error);
        alert('Failed to update photo. Please try again.');
      }
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      dateOfBirth: '',
      diagnosis: '',
      photo: '',
      emergencyContact: {
        name: '',
        phone: '',
        relationship: ''
      },
      admissionDate: new Date().toISOString().split('T')[0]
    });
    setRegisteredResidentId(null);
    setPhotoURL(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name || !formData.dateOfBirth || !formData.diagnosis) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      // Prepare emergency contact data
      const emergencyContact = formData.emergencyContact.name ? {
        name: formData.emergencyContact.name,
        phone: formData.emergencyContact.phone,
        relationship: formData.emergencyContact.relationship
      } : null;

      const response = await api.post('/residents', {
        name: formData.name,
        dateOfBirth: formData.dateOfBirth,
        diagnosis: formData.diagnosis,
        photo: formData.photo || null,
        emergencyContact: emergencyContact,
        admissionDate: formData.admissionDate
      });

      if (response.data.success) {
        const newResident = response.data.data;
        setRegisteredResidentId(newResident.id);
        onSuccess(newResident);
        setFormData({
          name: '',
          dateOfBirth: '',
          diagnosis: '',
          photo: '',
          emergencyContact: {
            name: '',
            phone: '',
            relationship: ''
          },
          admissionDate: new Date().toISOString().split('T')[0]
        });
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to register resident');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-md shadow-lg card-modal bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {registeredResidentId ? 'Upload Photo (Optional)' : 'Register New Resident'}
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {!registeredResidentId && (
            <form onSubmit={handleSubmit} className="space-y-4 max-h-96 overflow-y-auto">
              <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Enter resident's full name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
              <input
                type="date"
                value={formData.dateOfBirth || ''}
                onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
              {formData.dateOfBirth && (
                <p className="text-xs text-gray-500 mt-1">
                  Age: {Math.floor((new Date().getTime() - new Date(formData.dateOfBirth).getTime()) / (365.25 * 24 * 60 * 60 * 1000))} years
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Diagnosis *
              </label>
              <textarea
                name="diagnosis"
                value={formData.diagnosis}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Enter medical diagnosis"
                rows={3}
                required
              />
            </div>

            {registeredResidentId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Profile Photo (Optional)
                </label>
                <ObjectUploader
                  maxNumberOfFiles={1}
                  maxFileSize={5242880}
                  onGetUploadParameters={handleGetUploadParameters}
                  onComplete={handleUploadComplete}
                  buttonClassName="w-full px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>{photoURL ? 'Photo Uploaded ✓' : 'Upload Photo'}</span>
                  </div>
                </ObjectUploader>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Admission Date
              </label>
              <input
                type="date"
                name="admissionDate"
                value={formData.admissionDate}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="space-y-3 border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700">Emergency Contact (optional)</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Name
                </label>
                <input
                  type="text"
                  name="emergencyContact.name"
                  value={formData.emergencyContact.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Emergency contact name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="emergencyContact.phone"
                  value={formData.emergencyContact.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Emergency contact phone"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relationship
                </label>
                <input
                  type="text"
                  name="emergencyContact.relationship"
                  value={formData.emergencyContact.relationship}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g., Daughter, Son, Spouse"
                />
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Registering...' : 'Register Resident'}
              </button>
            </div>
          </form>
          )}

          {registeredResidentId && (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-800">Resident registered successfully!</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Profile Photo (Optional)
                </label>
                <ObjectUploader
                  maxNumberOfFiles={1}
                  maxFileSize={5242880}
                  onGetUploadParameters={handleGetUploadParameters}
                  onComplete={handleUploadComplete}
                  buttonClassName="w-full px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>{photoURL ? 'Photo Uploaded ✓' : 'Upload Photo'}</span>
                  </div>
                </ObjectUploader>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {photoURL ? 'Done' : 'Skip Photo & Close'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResidentRegistrationForm;